import { request } from '../request';

// ==========================================
// 前台/会员与积分 API
// AI视频制作OPC平台 API
// ==========================================

// 用于在途请求去重 (In-flight request deduplication)，防止 React 严格模式或双重挂载引发重复网络请求
const inFlightRequests = new Map<string, Promise<any>>();

function getDeduplicatedRequest<T>(cacheKey: string, requestFn: () => Promise<T>): Promise<T> {
  if (inFlightRequests.has(cacheKey)) {
    return inFlightRequests.get(cacheKey)!;
  }

  const promise = requestFn().finally(() => {
    setTimeout(() => {
      inFlightRequests.delete(cacheKey);
    }, 300);
  });

  inFlightRequests.set(cacheKey, promise);
  return promise;
}

/**
 * 查询我的会员订阅与积分余额
 * GET /membership/me
 *
 * @returns 会员订阅状态及积分钱包信息
 */
export function fetchMembershipMe() {
  return getDeduplicatedRequest('membership:me', () =>
    request<Api.Membership.MembershipMeResponse>({
      url: '/membership/me',
      method: 'get'
    })
  );
}

/**
 * 查询我的会员订单
 * GET /membership/orders
 *
 * @param params 分页查询参数 (page, page_size)
 * @returns 会员订单分页列表
 */
export function fetchMembershipOrders(params?: Api.Membership.QueryOrdersParams) {
  const cacheKey = `membership:orders:${params?.page || 1}:${params?.page_size || 20}`;
  return getDeduplicatedRequest(cacheKey, () =>
    request<Api.Membership.OrderPageResponse>({
      url: '/membership/orders',
      method: 'get',
      params
    })
  );
}

/**
 * 创建会员购买订单
 * POST /membership/orders
 *
 * @param data 订单请求体 (plan_id, idempotency_key)
 * @returns 创建成功的会员订单信息
 */
export function createMembershipOrder(data: Api.Membership.CreateMembershipOrderRequest) {
  return request<Api.Membership.OrderResponse>({
    url: '/membership/orders',
    method: 'post',
    data
  });
}

/**
 * 发起会员订单支付 (支付宝/微信扫码)
 * POST /membership/orders/{id}/pay
 *
 * @param id 会员订单 ID
 * @param params 支付渠道 query 参数 (payment_channel: ALIPAY | WECHAT)
 * @returns 支付二维码等支付信息
 */
export function payMembershipOrder(id: string | number, params?: Api.Membership.PayOrderQueryParams) {
  return request<Api.Membership.PaymentResponse>({
    url: `/membership/orders/${id}/pay`,
    method: 'post',
    params
  });
}

/**
 * 查询会员订单支付状态
 * GET /membership/orders/{id}/payment-status
 *
 * @param id 会员订单 ID
 * @returns 订单支付状态
 */
export function fetchMembershipOrderPaymentStatus(id: string | number) {
  return request<Api.Membership.PaymentStatusResponse>({
    url: `/membership/orders/${id}/payment-status`,
    method: 'get'
  });
}

/**
 * 查询可购买会员套餐
 * GET /membership/plans
 *
 * @param params 筛选参数 (subject_type: PERSONAL | ENTERPRISE)
 * @returns 可购买的会员套餐列表
 */
export function fetchMembershipPlans(params?: Api.Membership.QueryPlansParams) {
  const cacheKey = `membership:plans:${params?.subject_type || 'PERSONAL'}`;
  return getDeduplicatedRequest(cacheKey, () =>
    request<Api.Membership.PlanListResponse>({
      url: '/membership/plans',
      method: 'get',
      params
    })
  );
}

/**
 * 每日签到领取积分
 * POST /points/check-ins
 *
 * @returns 签到结果与积分奖励
 */
export function checkInPoints() {
  return request<Api.Membership.CheckInResponse>({
    url: '/points/check-ins',
    method: 'post'
  });
}

/**
 * 查询每日签到状态
 * GET /points/check-ins/status
 *
 * @returns 当前签到状态与奖励计划
 */
export function fetchCheckInStatus() {
  return getDeduplicatedRequest('points:check-ins:status', () =>
    request<Api.Membership.CheckInResponse>({
      url: '/points/check-ins/status',
      method: 'get'
    })
  );
}

/**
 * 查询会员积分流水
 * GET /points/membership-ledgers
 *
 * @param params 分页查询参数 (page, page_size)
 * @returns 会员积分变动流水
 */
export function fetchMembershipLedgers(params?: Api.Membership.QueryLedgerParams) {
  const cacheKey = `points:membership-ledgers:${params?.page || 1}:${params?.page_size || 20}`;
  return getDeduplicatedRequest(cacheKey, () =>
    request<Api.Membership.LedgerPageResponse>({
      url: '/points/membership-ledgers',
      method: 'get',
      params
    })
  );
}

/**
 * 查询我的积分购买订单
 * GET /points/orders
 *
 * @param params 分页查询参数 (page, page_size)
 * @returns 积分购买订单列表
 */
export function fetchPointOrders(params?: Api.Membership.QueryOrdersParams) {
  const cacheKey = `points:orders:${params?.page || 1}:${params?.page_size || 20}`;
  return getDeduplicatedRequest(cacheKey, () =>
    request<Api.Membership.OrderPageResponse>({
      url: '/points/orders',
      method: 'get',
      params
    })
  );
}

/**
 * 创建积分购买订单
 * POST /points/orders
 *
 * @param data 请求体 (product_id, idempotency_key)
 * @returns 积分订单信息
 */
export function createPointOrder(data: Api.Membership.CreatePointOrderRequest) {
  return request<Api.Membership.OrderResponse>({
    url: '/points/orders',
    method: 'post',
    data
  });
}

/**
 * 发起积分订单支付 (支付宝/微信扫码)
 * POST /points/orders/{id}/pay
 *
 * @param id 积分订单 ID
 * @param params 支付渠道 query 参数 (payment_channel: ALIPAY | WECHAT)
 * @returns 支付二维码等支付信息
 */
export function payPointOrder(id: string | number, params?: Api.Membership.PayOrderQueryParams) {
  return request<Api.Membership.PaymentResponse>({
    url: `/points/orders/${id}/pay`,
    method: 'post',
    params
  });
}

/**
 * 查询积分订单支付状态
 * GET /points/orders/{id}/payment-status
 *
 * @param id 积分订单 ID
 * @returns 订单支付状态
 */
export function fetchPointOrderPaymentStatus(id: string | number) {
  return request<Api.Membership.PaymentStatusResponse>({
    url: `/points/orders/${id}/payment-status`,
    method: 'get'
  });
}

/**
 * 查询可购买积分商品
 * GET /points/products
 *
 * @returns 可购买积分商品列表
 */
export function fetchPointProducts() {
  return getDeduplicatedRequest('points:products', () =>
    request<Api.Membership.PointProductListResponse>({
      url: '/points/products',
      method: 'get'
    })
  );
}

/**
 * 查询购买积分流水
 * GET /points/purchased-ledgers
 *
 * @param params 分页查询参数 (page, page_size)
 * @returns 充值/购买积分变动流水
 */
export function fetchPurchasedLedgers(params?: Api.Membership.QueryLedgerParams) {
  const cacheKey = `points:purchased-ledgers:${params?.page || 1}:${params?.page_size || 20}`;
  return getDeduplicatedRequest(cacheKey, () =>
    request<Api.Membership.LedgerPageResponse>({
      url: '/points/purchased-ledgers',
      method: 'get',
      params
    })
  );
}

/**
 * 查询我的积分钱包
 * GET /points/wallet
 *
 * @returns 积分钱包余额 (会员积分、购买积分、总积分)
 */
export function fetchPointsWallet() {
  return getDeduplicatedRequest('points:wallet', () =>
    request<Api.Membership.WalletResponse>({
      url: '/points/wallet',
      method: 'get'
    })
  );
}

/**
 * 查询全部积分流水
 * GET /points/ledgers
 *
 * @param params 分页查询参数 (page, page_size)
 * @returns 全部积分流水
 */
export function fetchPointLedgers(params?: Api.Membership.QueryLedgerParams) {
  const cacheKey = `points:ledgers:${params?.page || 1}:${params?.page_size || 20}`;
  return getDeduplicatedRequest(cacheKey, () =>
    request<Api.Membership.LedgerPageResponse>({
      url: '/points/ledgers',
      method: 'get',
      params
    })
  );
}

/**
 * 微信会员与积分订单支付回调
 * POST /membership/wechat/payment/callback
 *
 * @param data 回调数据
 * @returns 回调响应
 */
export function wechatPaymentCallback(data?: any) {
  return request<string>({
    url: '/membership/wechat/payment/callback',
    method: 'post',
    data
  });
}

/**
 * 支付宝会员与积分订单支付回调
 * POST /membership/payment/callback
 *
 * @param data 回调数据
 * @returns 回调响应
 */
export function alipayPaymentCallback(data?: any) {
  return request<string>({
    url: '/membership/payment/callback',
    method: 'post',
    data
  });
}
